import { Icon } from '../../components/Icon.tsx';
import type {
  AppProvisioning,
  WapsignRole,
  WapmeetRole,
  WapstatRole,
  WapstatZoneRole,
  WapproRole,
  WapdocsRole,
} from './api.ts';

// Local draft state for the "App access" block. Kept flat so the host form can
// own it with plain useState. Build the API `AppProvisioning` shape with
// `toAppProvisioning`.
export interface AppAccessState {
  wapsign: boolean;
  wapsignRole: WapsignRole;
  wapmeet: boolean;
  wapmeetRole: WapmeetRole;
  wapmeetIsOrgAdmin: boolean;
  wapstat: boolean;
  wapstatRole: WapstatRole | null;
  wapstatZoneRole: WapstatZoneRole | null;
  wapstatIsOrgAdmin: boolean;
  wappro: boolean;
  wapproRole: WapproRole;
  wapdocs: boolean;
  wapdocsRole: WapdocsRole;
}

export const emptyAppAccess: AppAccessState = {
  wapsign: false,
  wapsignRole: 'USER',
  wapmeet: false,
  wapmeetRole: 'MEMBER',
  wapmeetIsOrgAdmin: false,
  wapstat: false,
  wapstatRole: 'ENUMERATOR',
  wapstatZoneRole: null,
  wapstatIsOrgAdmin: false,
  wappro: false,
  wapproRole: 'STAFF',
  wapdocs: false,
  wapdocsRole: 'USER',
};

export function toAppProvisioning(s: AppAccessState): AppProvisioning | undefined {
  const out: AppProvisioning = {};
  if (s.wapsign) out.wapsign = { role: s.wapsignRole };
  if (s.wapmeet) out.wapmeet = { role: s.wapmeetRole, isOrgAdmin: s.wapmeetIsOrgAdmin };
  if (s.wapstat) out.wapstat = { role: s.wapstatRole, zoneRole: s.wapstatZoneRole, isOrgAdmin: s.wapstatIsOrgAdmin };
  if (s.wappro) out.wappro = { role: s.wapproRole };
  if (s.wapdocs) out.wapdocs = { role: s.wapdocsRole };
  return out.wapsign || out.wapmeet || out.wapstat || out.wappro || out.wapdocs ? out : undefined;
}

// WapStat rejects an account with no census role, no zone role and no org-admin
// flag; surface that before the invite is sent.
export function appAccessError(s: AppAccessState): string | null {
  return s.wapstat && !s.wapstatRole && !s.wapstatZoneRole && !s.wapstatIsOrgAdmin
    ? 'Give at least one WapStat role'
    : null;
}

const WAPMEET_ROLE_OPTIONS: { value: WapmeetRole; label: string }[] = [
  { value: 'PRESIDENT', label: 'President' },
  { value: 'VICE_PRESIDENT', label: 'Vice President' },
  { value: 'MEMBER', label: 'Member' },
  { value: 'SECRETARY_GENERAL', label: 'Secretary General' },
  { value: 'BUREAU_STAFF', label: 'Bureau Staff' },
  { value: 'VIEWER', label: 'Viewer' },
  { value: 'WDC_PRESIDENT', label: 'WDC President' },
  { value: 'WDC_VICE_PRESIDENT', label: 'WDC Vice President' },
  { value: 'WDC_MEMBER', label: 'WDC Member' },
];

const WAPSTAT_ROLE_OPTIONS: { value: WapstatRole; label: string }[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'ENUMERATOR', label: 'Enumerator' },
];

const WAPSTAT_ZONE_ROLE_OPTIONS: { value: WapstatZoneRole; label: string }[] = [
  { value: 'COUNCILLOR', label: 'Councillor' },
  { value: 'SECRETARY_GENERAL', label: 'Secretary General' },
  { value: 'STAFF', label: 'Staff' },
];

const WAPPRO_ROLE_OPTIONS: { value: WapproRole; label: string }[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'STAFF', label: 'Staff' },
];

export function AppAccessFields({
  value,
  onChange,
  hasWapsign,
  hasWapmeet,
  hasWapstat,
  hasWappro,
  hasWapdocs,
}: {
  value: AppAccessState;
  onChange: (next: AppAccessState) => void;
  hasWapsign: boolean;
  hasWapmeet: boolean;
  hasWapstat: boolean;
  hasWappro: boolean;
  hasWapdocs: boolean;
}): JSX.Element {
  const set = (patch: Partial<AppAccessState>) => onChange({ ...value, ...patch });
  const selectCls = 'select w-auto py-1.5';
  const checkboxCls = 'size-4 shrink-0 rounded accent-primary';

  return (
    <div className="rounded-xl bg-[hsl(var(--surface-container-low))] p-4 space-y-3">
      <p className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Icon name="apps" size={18} /> App access
      </p>
      <p className="text-xs text-muted-foreground">
        Create accounts in these apps when the invitee sets their password. One password everywhere.
      </p>

      {/* WapSign */}
      <div className="space-y-1.5">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className={checkboxCls}
            checked={value.wapsign}
            disabled={!hasWapsign}
            onChange={(e) => set({ wapsign: e.target.checked })}
          />
          <span className={hasWapsign ? 'text-foreground' : 'text-muted-foreground'}>
            Create WapSign account
          </span>
          {!hasWapsign && <span className="text-xs text-muted-foreground">(not linked)</span>}
        </label>
        {value.wapsign && hasWapsign && (
          <select
            aria-label="WapSign role"
            value={value.wapsignRole}
            onChange={(e) => set({ wapsignRole: e.target.value as WapsignRole })}
            className={`ml-6 ${selectCls}`}
          >
            <option value="USER">User</option>
            <option value="ORG_ADMIN">Org admin</option>
          </select>
        )}
      </div>

      {/* WapMeet */}
      <div className="space-y-1.5">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className={checkboxCls}
            checked={value.wapmeet}
            disabled={!hasWapmeet}
            onChange={(e) => set({ wapmeet: e.target.checked })}
          />
          <span className={hasWapmeet ? 'text-foreground' : 'text-muted-foreground'}>
            Create WapMeet account
          </span>
          {!hasWapmeet && <span className="text-xs text-muted-foreground">(not linked)</span>}
        </label>
        {value.wapmeet && hasWapmeet && (
          <div className="ml-6 flex flex-wrap items-center gap-3">
            <select
              aria-label="WapMeet role"
              value={value.wapmeetRole}
              onChange={(e) => set({ wapmeetRole: e.target.value as WapmeetRole })}
              className={selectCls}
            >
              {WAPMEET_ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                className={checkboxCls}
                checked={value.wapmeetIsOrgAdmin}
                onChange={(e) => set({ wapmeetIsOrgAdmin: e.target.checked })}
              />
              Org admin
            </label>
          </div>
        )}
      </div>

      {/* WapStat */}
      <div className="space-y-1.5">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className={checkboxCls}
            checked={value.wapstat}
            disabled={!hasWapstat}
            onChange={(e) => set({ wapstat: e.target.checked })}
          />
          <span className={hasWapstat ? 'text-foreground' : 'text-muted-foreground'}>
            Create WapStat account
          </span>
          {!hasWapstat && <span className="text-xs text-muted-foreground">(not linked)</span>}
        </label>
        {value.wapstat && hasWapstat && (
          <div className="ml-6 flex flex-wrap items-center gap-3">
            <select
              aria-label="WapStat census role"
              value={value.wapstatRole ?? ''}
              onChange={(e) => set({ wapstatRole: (e.target.value || null) as WapstatRole | null })}
              className={selectCls}
            >
              <option value="">Census: None</option>
              {WAPSTAT_ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  Census: {o.label}
                </option>
              ))}
            </select>
            <select
              aria-label="WapStat zone role"
              value={value.wapstatZoneRole ?? ''}
              onChange={(e) => set({ wapstatZoneRole: (e.target.value || null) as WapstatZoneRole | null })}
              className={selectCls}
            >
              <option value="">Zone: None</option>
              {WAPSTAT_ZONE_ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  Zone: {o.label}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                className={checkboxCls}
                checked={value.wapstatIsOrgAdmin}
                onChange={(e) => set({ wapstatIsOrgAdmin: e.target.checked })}
              />
              Org admin
            </label>
          </div>
        )}
      </div>

      {appAccessError(value) && (
        <p role="alert" className="ml-6 text-xs text-destructive">
          {appAccessError(value)}
        </p>
      )}

      {/* WapPro */}
      <div className="space-y-1.5">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className={checkboxCls}
            checked={value.wappro}
            disabled={!hasWappro}
            onChange={(e) => set({ wappro: e.target.checked })}
          />
          <span className={hasWappro ? 'text-foreground' : 'text-muted-foreground'}>
            Create WapPro account
          </span>
          {!hasWappro && <span className="text-xs text-muted-foreground">(not linked)</span>}
        </label>
        {value.wappro && hasWappro && (
          <select
            aria-label="WapPro role"
            value={value.wapproRole}
            onChange={(e) => set({ wapproRole: e.target.value as WapproRole })}
            className={`ml-6 ${selectCls}`}
          >
            {WAPPRO_ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* WapDocs */}
      <div className="space-y-1.5">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className={checkboxCls}
            checked={value.wapdocs}
            disabled={!hasWapdocs}
            onChange={(e) => set({ wapdocs: e.target.checked })}
          />
          <span className={hasWapdocs ? 'text-foreground' : 'text-muted-foreground'}>
            Create WapDocs account
          </span>
          {!hasWapdocs && <span className="text-xs text-muted-foreground">(not linked)</span>}
        </label>
        {value.wapdocs && hasWapdocs && (
          <select
            aria-label="WapDocs role"
            value={value.wapdocsRole}
            onChange={(e) => set({ wapdocsRole: e.target.value as WapdocsRole })}
            className={`ml-6 ${selectCls}`}
          >
            <option value="USER">User</option>
            <option value="ORG_ADMIN">Org admin</option>
          </select>
        )}
      </div>
    </div>
  );
}

// Small WS/WM chips for the pending-invites list.
export function ProvisioningChips({ prov }: { prov?: AppProvisioning | null }): JSX.Element | null {
  if (!prov || (!prov.wapsign && !prov.wapmeet && !prov.wapstat && !prov.wappro && !prov.wapdocs)) return null;
  const chip =
    'inline-flex items-center rounded-lg bg-[hsl(var(--surface-container-highest))] px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground';
  return (
    <span className="inline-flex items-center gap-1">
      {prov.wapsign && <span className={chip}>WS</span>}
      {prov.wapmeet && <span className={chip}>WM</span>}
      {prov.wapstat && <span className={chip}>ST</span>}
      {prov.wappro && <span className={chip}>WP</span>}
      {prov.wapdocs && <span className={chip}>WD</span>}
    </span>
  );
}
